"use client";

import { motion } from "framer-motion";
import {
  FaFacebookF,
  FaInstagram,
  FaTwitter,
  FaPinterest,
  FaYoutube,
  FaLinkedinIn,
} from "react-icons/fa";
import Image from "next/image";

interface FooterLink {
  id: number;
  url: string;
  newTab: boolean;
  text: string;
  social?: string;
}

interface CategoryLink {
  id: string;
  name: string;
  slug: string;
  url: string;
  text: string;
  newTab: boolean;
}

interface NavLink {
  id: number;
  label: string;
  url: string;
  newTab: boolean;
}

interface NavigationSection {
  id: number;
  title: string;
  isLive: boolean;
  links: NavLink[];
}

interface AddressUse {
  id: number;
  text: string;
  address: string;
  country: string;
  email: string;
  Phone: string;
}

type Address = Partial<AddressUse>;

function RenderSocialIcon({ social }: { social?: string }) {
  const iconClass =
    "text-white hover:text-gray-400 transition-colors duration-300";

  switch (social) {
    case "TWITTER":
      return <FaTwitter className={iconClass} />;
    case "INSTAGRAM":
      return <FaInstagram className={iconClass} />;
    case "PINTEREST":
      return <FaPinterest className={iconClass} />;
    case "FACEBOOK":
      return <FaFacebookF className={iconClass} />;
    case "YOUTUBE":
      return <FaYoutube className={iconClass} />;
    case "LINKEDIN":
      return <FaLinkedinIn className={iconClass} />;
    default:
      return null;
  }
}

export default function Footer_Layout1({
  logoUrl,
  logoText,
  menuLinks,
  legalLinks,
  socialLinks,
  addresses,
  productLinks,
  eCommerceLinks,
  navigation,
}: {
  logoUrl: string | null;
  logoText: string | null;
  menuLinks: FooterLink[];
  productLinks: CategoryLink[];
  legalLinks: FooterLink[];
  eCommerceLinks: FooterLink[];
  socialLinks: FooterLink[];
  addresses: Address[];
  navigation: NavigationSection[];
}) {

  // ================= SECTION AVAILABILITY =================

  const hasNavigation = navigation?.length > 0;
  const hasMenu = menuLinks?.length > 0;
  const hasProducts = productLinks?.length > 0;
  const hasEcommerce = eCommerceLinks?.length > 0;
  const hasAddresses = addresses?.length > 0;
  const hasLegal = legalLinks?.length > 0;

  const staticSectionsCount = [
    hasMenu,
    hasProducts,
    hasEcommerce,
  ].filter(Boolean).length;

  // Address used as filler only if some static sections exist but not all
  const useAddressAsFiller =
    hasAddresses && staticSectionsCount > 0 && staticSectionsCount < 3;

  // ================= RIGHT SIDE LAYOUT =================

  const sections: string[] = [];

  if (hasMenu) sections.push("menu");
  if (hasProducts) sections.push("products");
  if (hasEcommerce) sections.push("ecommerce");
  if (useAddressAsFiller) sections.push("address");

  let gridClass = "";
  if (sections.length === 1)
    gridClass = "grid-cols-1 max-w-md mx-auto text-center";
  else if (sections.length === 2)
    gridClass = "grid-cols-1 sm:grid-cols-2 gap-8";
  else if (sections.length === 3)
    gridClass = "grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-8";
  else
    gridClass = "grid-cols-1 sm:grid-cols-2 md:grid-cols-4 gap-6";

  // ================= BOTTOM ADDRESS CONTROL =================

  const showBottomAddresses = hasAddresses && !useAddressAsFiller;

  const bottomAddressLayout =
    addresses?.length === 1
      ? "flex justify-center text-center max-w-2xl mx-auto"
      : addresses?.length === 2
      ? "grid grid-cols-1 sm:grid-cols-2 gap-8"
      : "grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-8";

  return (
   <footer className=" bg-black text-gray-100 border-t border-gray-800">

      <div className="container mx-auto px-6 md:px-8 py-16">

        {/* ================= NAVIGATION ================= */}
        {hasNavigation && (
          <div className="mb-16">
            <h2 className="text-sm uppercase tracking-[0.3em] text-gray-400 mb-8">
              Navigate
            </h2>

            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-10">
              {navigation.map((section) => (
                <div key={section.id}>
                  <h3 className="text-sm uppercase tracking-wider mb-4">
                    {section.title}
                  </h3>
                  <ul className="space-y-2">
                    {section.links.map((link) => (
                      <li key={link.id}>
                        <a
                          href={link.url}
                          target={link.newTab ? "_blank" : "_self"}
                          className="text-gray-400 hover:text-white text-sm transition"
                        >
                          {link.label}
                        </a>
                      </li>
                    ))}
                  </ul>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* ================= NEWSLETTER + STATIC ================= */}
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-16">

          {/* Newsletter */}
          <div className="lg:col-span-5">
            <h3 className="uppercase tracking-widest mb-6 text-sm">
              Newsletter
            </h3>
            <p className="text-gray-200 text-sm mb-6">
              Subscribe to receive updates on new arrivals and offers.
            </p>

            <div className="flex flex-col sm:flex-row gap-3 mb-6">
              <input
                type="email"
                placeholder="Your email"
                className="flex-1 border border-gray-700 bg-gray-900 px-4 py-3 text-sm focus:outline-none focus:border-white"
              />
              <button className="border border-white px-6 py-3 text-sm hover:bg-white hover:text-black transition">
                Subscribe
              </button>
            </div>

            {socialLinks?.length > 0 && (
              <div className="flex gap-5">
                {socialLinks.map((link) => (
                  <a
                    key={link.id}
                    href={link.url}
                    target={link.newTab ? "_blank" : "_self"}
                  >
                    <RenderSocialIcon social={link.social} />
                  </a>
                ))}
              </div>
            )}
          </div>

          {/* Static Sections + Address Filler */}
          {sections.length > 0 && (
            <div className="lg:col-span-7">
              <div className={`grid ${gridClass}`}>
                {sections.map((type) => {

                  if (type === "menu")
                    return (
                      <div key="menu">
                        <h3 className="uppercase text-sm mb-4">Menu</h3>
                        <ul className="space-y-2">
                          {menuLinks.map((l) => (
                            <li key={l.id}>
                              <a href={l.url} className="text-gray-400 hover:text-white text-sm">
                                {l.text}
                              </a>
                            </li>
                          ))}
                        </ul>
                      </div>
                    );

                  if (type === "products")
                    return (
                      <div key="products">
                        <h3 className="uppercase text-sm mb-4">Products</h3>
                        <ul className="space-y-2">
                          {productLinks.map((l) => (
                            <li key={l.id}>
                              <a href={l.url} className="text-gray-300 hover:text-white text-sm">
                                {l.text || l.name}
                              </a>
                            </li>
                          ))}
                        </ul>
                      </div>
                    );

                  if (type === "ecommerce")
                    return (
                      <div key="ecommerce">
                        <h3 className="uppercase text-sm mb-4">E-Commerce</h3>
                        <ul className="space-y-2">
                          {eCommerceLinks.map((l) => (
                            <li key={l.id}>
                              <a href={l.url} className="text-gray-400 hover:text-white text-sm">
                                {l.text}
                              </a>
                            </li>
                          ))}
                        </ul>
                      </div>
                    );

                  if (type === "address")
                    return (
                      <div key="address">
                        <h3 className="uppercase text-sm mb-4">
                          {addresses[0]?.text || "Visit Us"}
                        </h3>
                        <p className="text-gray-400 text-sm">{addresses[0]?.address}</p>
                        <p className="text-gray-400 text-sm">{addresses[0]?.country}</p>
                        <p className="text-gray-500 text-sm">
                          {addresses[0]?.email} | {addresses[0]?.Phone}
                        </p>
                      </div>
                    );

                  return null;
                })}
              </div>
            </div>
          )}
        </div>

        {/* ================= BOTTOM SECTION ================= */}
        {(showBottomAddresses || hasLegal || logoUrl) && (
          <div className="mt-16 pt-8 border-t border-gray-900">

            {showBottomAddresses && (
              <div className={`${bottomAddressLayout} mb-10`}>
                {addresses.map((a) => (
                  <div key={a.id} className="text-gray-400 text-xs">
                    <p className="uppercase text-gray-500 mb-2">
                      {a.text || "Office"}
                    </p>
                    <p>{a.address}</p>
                    <p>{a.country}</p>
                    <p>{a.email} | {a.Phone}</p>
                  </div>
                ))}
              </div>
            )}

            {logoUrl && (
              <div className={`flex justify-center ${hasLegal ? "mb-6" : ""}`}>
                <Image
                  src={logoUrl}
                  alt={logoText || "Brand"}
                  width={200}
                  height={60}
                  className="opacity-70 hover:opacity-100 transition"
                />
              </div>
            )}

            {hasLegal && (
              <div className="flex flex-wrap justify-center gap-6 mb-4">
                {legalLinks.map((l) => (
                  <a
                    key={l.id}
                    href={l.url}
                    target={l.newTab ? "_blank" : "_self"}
                    className="text-xs text-gray-600 hover:text-gray-400 uppercase transition"
                  >
                    {l.text}
                  </a>
                ))}
              </div>
            )}

            {(logoUrl || hasLegal) && (
              <p className="text-xs text-gray-100 text-center">
                © {new Date().getFullYear()} {logoText || "Brand"}.
              </p>
            )}
          </div>
        )}
      </div>
    </footer>
  );
}
